/* BIA Swipe Edit — transform card stack.
   Gesture is bound to the STAGE in the capture phase, so a drag can begin from
   any part of the active card: image, gradient, title, price, or the CTA.
   Axis lock (9px + 1.15 ratio) means vertical page scrolling is never trapped.
   Without JS the CSS scroll-snap rail remains fully usable. */
(function () {
  var BOUND = 'biaSwipeBound';
  var LOCK = 9, RATIO = 1.15, DIST = 0.26, VEL = 0.5, TAP = 8;

  function init(root) {
    if (!root || root[BOUND]) return;
    root[BOUND] = true;

    var stage = root.querySelector('.swp__stage');
    var track = root.querySelector('[data-swp-track]');
    var cards = Array.prototype.slice.call(root.querySelectorAll('[data-swp-card]'));
    if (!stage || !track || !cards.length) return;

    var prevBtn = root.querySelector('[data-swp-prev]');
    var nextBtn = root.querySelector('[data-swp-next]');
    var posEl = root.querySelector('[data-swp-pos]');
    var barEl = root.querySelector('[data-swp-bar]');
    var liveEl = root.querySelector('[data-swp-live]');
    var restart = root.querySelector('[data-swp-restart]');
    var total = parseInt(root.getAttribute('data-total'), 10) || cards.length;
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var index = 0, cleanups = [], raf = null, drag = null, didDrag = false;

    function on(el, ev, fn, opt) {
      el.addEventListener(ev, fn, opt);
      cleanups.push(function () { el.removeEventListener(ev, fn, opt); });
    }
    function pad(n) { return (n < 10 ? '0' : '') + n; }

    root.classList.add('is-deck');

    /* ---- image loading: active + next two only ---- */
    function hydrate() {
      for (var i = 0; i < cards.length; i++) {
        var img = cards[i].querySelector('img');
        if (!img) continue;
        var near = (i >= index - 1 && i <= index + 2);
        if (near && img.dataset.src) { img.src = img.dataset.src; delete img.dataset.src; }
        if (near && img.getAttribute('loading') === 'lazy') img.setAttribute('loading', 'eager');
      }
    }

    function layout() {
      for (var i = 0; i < cards.length; i++) {
        var c = cards[i], d = i - index;
        c.style.removeProperty('--drag-x');
        c.style.removeProperty('--drag-y');
        c.style.removeProperty('--drag-progress');
        c.style.removeProperty('--card-rotation');
        var vis = (d >= 0 && d <= 2);
        c.style.visibility = vis ? '' : 'hidden';
        c.style.setProperty('--depth', String(d < 0 ? 0 : d));
        c.style.zIndex = String(50 - Math.max(d, 0));
        c.classList.toggle('is-active', d === 0);
        c.setAttribute('aria-hidden', d === 0 ? 'false' : 'true');
        var focusables = c.querySelectorAll('a,button');
        for (var f = 0; f < focusables.length; f++) focusables[f].tabIndex = (d === 0 ? 0 : -1);
      }
      var isEnd = index >= total;
      if (posEl) posEl.textContent = isEnd ? 'End of the edit' : pad(index + 1) + ' / ' + total;
      if (barEl) barEl.style.width = Math.round(((index + 1) / (total + 1)) * 100) + '%';
      if (prevBtn) prevBtn.disabled = index <= 0;
      if (nextBtn) nextBtn.disabled = index >= cards.length - 1;
      if (liveEl) {
        var nm = cards[index] && cards[index].getAttribute('data-name');
        liveEl.textContent = isEnd ? 'End of the edit' : (nm || '') + ', ' + (index + 1) + ' of ' + total;
      }
      hydrate();
    }

    function fly(dir, done) {
      var c = cards[index];
      if (!c) { done && done(); return; }
      if (reduce) { done && done(); return; }
      c.classList.add('is-flying');
      c.style.setProperty('--drag-x', (dir * 125) + 'vw');
      c.style.setProperty('--drag-y', '-5vh');
      c.style.setProperty('--card-rotation', (dir * 11) + 'deg');
      var t = setTimeout(function () { c.classList.remove('is-flying'); done && done(); }, 390);
      cleanups.push(function () { clearTimeout(t); });
    }

    function spring() {
      var c = cards[index]; if (!c) return;
      c.classList.add('is-springing');
      c.style.setProperty('--drag-x', '0px');
      c.style.setProperty('--drag-y', '0px');
      c.style.setProperty('--drag-progress', '0');
      c.style.setProperty('--card-rotation', '0deg');
      var t = setTimeout(function () { c.classList.remove('is-springing'); }, 470);
      cleanups.push(function () { clearTimeout(t); });
    }

    function go(delta) {
      var target = index + delta;
      if (target < 0 || target > cards.length - 1) { spring(); return; }
      root.classList.add('has-swiped');
      fly(delta > 0 ? -1 : 1, function () { index = target; layout(); });
    }

    /* ---- gesture on the STAGE, capture phase: whole card is a handle ---- */
    on(stage, 'pointerdown', function (e) {
      if (e.button > 0) return;
      var c = cards[index];
      if (!c || c.style.visibility === 'hidden') return;
      if (!c.contains(e.target)) return;
      didDrag = false;
      drag = { id: e.pointerId, x: e.clientX, y: e.clientY, dx: 0, dy: 0,
               t: performance.now(), axis: null, card: c, w: c.offsetWidth || 320 };
    }, true);

    on(stage, 'pointermove', function (e) {
      if (!drag || e.pointerId !== drag.id) return;
      drag.dx = e.clientX - drag.x;
      drag.dy = e.clientY - drag.y;

      if (!drag.axis) {
        var ax = Math.abs(drag.dx), ay = Math.abs(drag.dy);
        if (ax < LOCK && ay < LOCK) return;
        if (ax > ay * RATIO) {
          drag.axis = 'x';
          didDrag = true;
          try { stage.setPointerCapture(e.pointerId); } catch (err) {}
          drag.card.classList.add('is-dragging');
          root.classList.add('is-drag');
        } else { drag = null; return; }   /* vertical: hand back to the page */
      }
      if (drag.axis !== 'x') return;
      if (e.cancelable) e.preventDefault();
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null; if (!drag) return;
        var p = Math.max(-1, Math.min(1, drag.dx / (drag.w * DIST)));
        var rot = Math.max(-7, Math.min(7, drag.dx * 0.032));
        var s = drag.card.style;
        s.setProperty('--drag-x', drag.dx + 'px');
        s.setProperty('--drag-y', (-Math.abs(drag.dx) * 0.06) + 'px');
        s.setProperty('--drag-progress', String(Math.abs(p)));
        s.setProperty('--drag-direction', drag.dx < 0 ? '-1' : '1');
        s.setProperty('--card-rotation', rot + 'deg');
      });
    }, { passive: false, capture: true });

    function release(e) {
      if (!drag || (e && e.pointerId !== drag.id)) return;
      var d = drag; drag = null;
      root.classList.remove('is-drag');
      d.card.classList.remove('is-dragging');
      try { stage.releasePointerCapture(d.id); } catch (err) {}
      if (d.axis !== 'x') return;
      var dt = Math.max(1, performance.now() - d.t);
      if (Math.abs(d.dx) > d.w * DIST || (Math.abs(d.dx) / dt) > VEL) go(d.dx < 0 ? 1 : -1);
      else spring();
    }
    on(stage, 'pointerup', release, true);
    on(stage, 'pointercancel', release, true);
    on(stage, 'lostpointercapture', function () { if (drag) release({ pointerId: drag.id }); }, true);

    /* a drag must never open the product; a tap must */
    on(stage, 'click', function (e) {
      if (didDrag) { e.preventDefault(); e.stopPropagation(); didDrag = false; }
    }, true);
    on(stage, 'dragstart', function (e) { e.preventDefault(); });

    /* ---- controls ---- */
    if (prevBtn) on(prevBtn, 'click', function () { go(-1); });
    if (nextBtn) on(nextBtn, 'click', function () { go(1); });
    if (restart) on(restart, 'click', function () { index = 0; layout(); track.focus(); });
    on(track, 'keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); go(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); go(-1); }
      else if (e.key === 'Home') { e.preventDefault(); index = 0; layout(); }
    });

    layout();

    /* ---- first-use cue, once per session ---- */
    function cue() {
      if (reduce) return;
      try { if (sessionStorage.getItem('biaSwipeCue')) return; sessionStorage.setItem('biaSwipeCue', '1'); } catch (e) {}
      var c = cards[0]; if (!c) return;
      c.classList.add('is-cue');
      c.style.setProperty('--drag-x', '-16px');
      c.style.setProperty('--card-rotation', '-1deg');
      var t1 = setTimeout(function () {
        c.style.setProperty('--drag-x', '0px');
        c.style.setProperty('--card-rotation', '0deg');
        var t2 = setTimeout(function () { c.classList.remove('is-cue'); }, 520);
        cleanups.push(function () { clearTimeout(t2); });
      }, 620);
      cleanups.push(function () { clearTimeout(t1); });
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (en) {
        if (en[0] && en[0].isIntersecting) { cue(); io.disconnect(); }
      }, { threshold: 0.4 });
      io.observe(stage);
      cleanups.push(function () { io.disconnect(); });
    } else { cue(); }

    root.biaSwipeDestroy = function () {
      cleanups.forEach(function (fn) { try { fn(); } catch (e) {} });
      cleanups = []; root[BOUND] = false; root.classList.remove('is-deck');
    };
  }

  function boot(scope) {
    var els = (scope || document).querySelectorAll('[data-bia-swipe]');
    Array.prototype.forEach.call(els, init);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector ? e.target.querySelector('[data-bia-swipe]') : null;
    if (el && el.biaSwipeDestroy) el.biaSwipeDestroy();
  });
})();
